<template>
    <div class="space-y-6">
        <participant-detail-section v-if="showContractLevel">
            {{ t('purchaseContract.additionalInformation') }}
        </participant-detail-section>

        <template
            v-for="contractConstraint in contractConstraints.contractConstraints
                .value"
            :key="contractConstraint"
        >
            <prime-message
                v-if="contractConstraint.attachmentRequirement === null"
                class="w-auto"
                :closable="false"
                :severity="
                    contractConstraint.constraintConfig.required
                        ? 'error'
                        : 'warn'
                "
            >
                {{ contractConstraint.constraintConfig.message }}
            </prime-message>

            <customer-constraint-attachment
                v-else
                :attachment-requirement="
                    contractConstraint.attachmentRequirement
                "
                :constraint-config="contractConstraint.constraintConfig"
                :show-errors="showInvalidQuestions"
                @replace="
                    contractConstraints.handleReplaceFileUpload(
                        contractConstraint.attachmentRequirement,
                        $event
                    )
                "
                @file="
                    contractConstraints.handleFileUpload(
                        contractConstraint.attachmentRequirement,
                        $event
                    )
                "
            />
        </template>

        <div
            v-if="questionFormData.contractQuestions.length"
            class="space-y-6"
        >
            <plan-question
                v-for="(
                    question, questionIndex
                ) in questionFormData.contractQuestions"
                :key="question.planQuestion.id"
                :question="question"
                :billing-interval="billingInterval"
                :billing-cycle-string="
                    questionData.terms.billingCycleString ?? ''
                "
                :show-invalid-questions="showInvalidQuestions"
                :unique-key="`${question.planQuestion.id}`"
                @update-pricing="$emit('updatePricing')"
                @update:question="updateContractValue(questionIndex, $event)"
            />
        </div>

        <div
            v-if="showEnrollmentLevel"
            class="space-y-6"
        >
            <div
                v-for="(enrollment, enrollmentIndex) in enrollmentLevelLoops"
                :key="`${enrollment.enrollmentSpecification.enrollmentTypeId} ${enrollment.enrollmentSpecification.sequence}`"
                class="space-y-6"
            >
                <participant-detail-section>
                    <span v-if="enrollment.enrollment !== undefined">
                        <span class="italic">{{
                            enrollment.enrollment.customerName
                        }}</span>
                        &mdash;
                        {{
                            `${enrollment.enrollmentSpecification.enrollmentTypeName} #${enrollment.enrollmentSpecification.sequence}`
                        }}
                    </span>
                    <span v-else>
                        {{
                            `${enrollment.enrollmentSpecification.enrollmentTypeName} #${enrollment.enrollmentSpecification.sequence}`
                        }}
                    </span>
                </participant-detail-section>

                <customer-constraint-attachment
                    v-if="enrollment.attachment !== null"
                    :attachment-requirement="enrollment.attachment"
                    :enrollment-constraints="enrollmentConstraints"
                    :constraint-config="
                        enrollmentConstraints.enrollmentConstraintConfigLookup.value.get(
                            enrollment.enrollmentSpecification.enrollmentTypeId
                        )
                    "
                    :show-errors="showInvalidQuestions"
                    @replace="
                        enrollmentConstraints.handleReplaceFileUpload(
                            enrollment.enrollmentSpecification.enrollmentTypeId,
                            enrollment.enrollmentSpecification.sequence,
                            enrollment.attachment,
                            $event
                        )
                    "
                    @file="
                        enrollmentConstraints.handleFileUpload(
                            enrollment.enrollmentSpecification.enrollmentTypeId,
                            enrollment.enrollmentSpecification.sequence,
                            enrollment.attachment,
                            $event
                        )
                    "
                />

                <plan-question
                    v-for="(question, questionIndex) in enrollment.questions"
                    :key="questionIndex"
                    :question="question"
                    :billing-interval="billingInterval"
                    :billing-cycle-string="
                        questionData.terms.billingCycleString ?? ''
                    "
                    :show-invalid-questions="showInvalidQuestions"
                    :unique-key="`${enrollment.enrollmentSpecification.enrollmentTypeId}_${enrollment.enrollmentSpecification.sequence}`"
                    @update-pricing="$emit('updatePricing')"
                    @update:question="
                        updateEnrollmentValue(
                            enrollmentIndex,
                            questionIndex,
                            $event
                        )
                    "
                />
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { graphql } from '~/resources/graphql'

export const AnswerPlanQuestionsQuestionFragment = graphql(/* GraphQL */ `
    fragment AnswerPlanQuestionsQuestionFragment on PlanQuestion {
        id
        translatedConfig(language: $language) {
            __typename
            ... on PlanTextQuestion {
                title
                body
                required
                size
                defaultAnswerString
            }
            ... on PlanParagraphQuestion {
                title
                body
                required
                size
                defaultAnswerString
            }
            ... on PlanBooleanQuestion {
                title
                body
                required
            }
            ... on PlanNumberQuestion {
                title
                body
                min
                max
                required
                defaultAnswerString
                unitPricing {
                    feePricingTotal
                    duesPricingTotal
                }
            }
            ... on PlanSingleChoiceQuestion {
                title
                body
                required
                questionInputType
                size
                defaultPlanQuestionOptionId
            }
            ... on PlanMultipleChoiceQuestion {
                title
                body
                required
                defaultPlanQuestionOptionId
            }
            ... on PlanPhoneNumberQuestion {
                title
                body
                required
            }
            ... on PlanEmailQuestion {
                title
                body
                required
            }
        }
        planQuestionOptions(active: true) {
            id
            translatedLabel(language: $language)
            pricingConfig {
                feePricingTotal
                duesPricingTotal
            }
        }
    }
`)

export const AnswerPlanQuestionsPlanFragment = graphql(/** GraphQL */ `
    fragment AnswerPlanQuestionsPlanFragment on Plan {
        terms {
            billingType
            billingInterval
            billingCycleString(language: $language)
            allowEmptyEnrollments
            enrollmentRanges {
                enrollmentType {
                    ageEnforcementMode
                }
            }
        }
        questionLayoutConfig(input: $input) {
            contractQuestions {
                id
                ...AnswerPlanQuestionsQuestionFragment
            }
            enrollmentQuestions {
                id
                ...AnswerPlanQuestionsQuestionFragment
            }
        }
    }
`)

export const AnswerPlanQuestionsPlanContractFragment = graphql(/** GraphQL */ `
    fragment AnswerPlanQuestionsPlanContractFragment on PlanContract {
        id
        properties {
            planQuestion {
                id
            }
            value
            planQuestionOptions {
                id
            }
            enrollment {
                customer {
                    id
                }
            }
        }
    }
`)
</script>

<script setup lang="ts">
import type { EnrollmentInputArray } from '~/composables/enrollment-input'
import { enrollmentInputArrayValidator } from '~/composables/enrollment-input'
import type {
    ContractQuestionFormData,
    QuestionFormData,
    QuestionValue
} from '~/composables/planQuestionFormData'
import type { PropType } from 'vue'
import { computed, watch } from 'vue'
import type { FragmentType } from '~/resources/graphql'
import { getFragmentData } from '~/resources/graphql'
import ParticipantDetailSection from '~/components/PurchaseContract/ParticipantDetails/ParticipantDetailSection.vue'
import { Decimal } from '~/composables/support/decimal'
import PlanQuestion from '~/components/PurchaseContract/ParticipantDetails/PlanQuestions/PlanQuestion.vue'
import type {
    AttachmentRequirement,
    EnrollmentConstraints
} from '~/components/PurchaseContract/ParticipantDetails/enrollment-constraint'
import CustomerConstraintAttachment from '~/components/PurchaseContract/ParticipantDetails/CustomerConstraintAttachment.vue'
import type { ContractConstraints } from '~/components/PurchaseContract/ParticipantDetails/contract-constraint'
import PrimeMessage from 'primevue/message'
import { useI18n } from 'vue-i18n'

const props = defineProps({
    plan: {
        type: Object as PropType<
            FragmentType<typeof AnswerPlanQuestionsPlanFragment>
        >,
        required: true
    },
    contract: {
        type: Object as PropType<
            FragmentType<typeof AnswerPlanQuestionsPlanContractFragment>
        >,
        required: true
    },
    currentEnrollments: {
        type: Array as PropType<EnrollmentInputArray>,
        required: true,
        validator: enrollmentInputArrayValidator
    },
    questionFormData: {
        type: Object as PropType<QuestionFormData>,
        required: true
    },
    showInvalidQuestions: {
        type: Boolean,
        required: true
    },
    enrollmentConstraints: {
        type: Object as PropType<EnrollmentConstraints>,
        required: true
    },
    contractConstraints: {
        type: Object as PropType<ContractConstraints>,
        required: true
    }
})

const { t } = useI18n({ useScope: 'global' })
const emit = defineEmits<{
    (e: 'update:questionFormData', payload: QuestionFormData): void
    (e: 'updatePricing'): void
}>()

const questionData = computed(() => {
    return getFragmentData(AnswerPlanQuestionsPlanFragment, props.plan)
})

const contractData = computed(() => {
    return getFragmentData(
        AnswerPlanQuestionsPlanContractFragment,
        props.contract
    )
})

// On initial component mount, we'll load contract property data
updateQuestionFormData(true)
// Without below line, single/multiple choice questions with pricing and default options don't register
emit('updatePricing')

// Watch for changes in enrollments to update the plan questions
watch(
    () => props.currentEnrollments,
    () => {
        // Don't load data from contract on enrollment change
        updateQuestionFormData(false)
    }
)

function updateQuestionFormData(loadContractData: boolean) {
    const planQuestionFormData: QuestionFormData = {
        contractQuestions: [],
        enrollmentQuestions: []
    }

    if (questionData.value.questionLayoutConfig === null) {
        emit('update:questionFormData', planQuestionFormData)
        return
    }

    const contractQuestions =
        questionData.value.questionLayoutConfig.contractQuestions

    const enrollmentQuestions =
        questionData.value.questionLayoutConfig.enrollmentQuestions

    // Add contract level questions
    planQuestionFormData.contractQuestions = contractQuestions.map(question => {
        // If we have an existing contract property for this question
        // We set to that property's value so that data is saved
        let existingQuestion: QuestionValue | undefined = undefined
        if (loadContractData) {
            const existingProperty = contractData.value.properties.find(
                property => property.planQuestion?.id === question.id
            )
            if (existingProperty !== undefined) {
                existingQuestion = {
                    propertyValue: existingProperty.value,
                    planQuestionOptionIds:
                        existingProperty.planQuestionOptions.map(
                            option => option.id
                        ),
                    invalid: false
                }
            }
        } else {
            // Else we load existing data in questionFormData
            existingQuestion = props.questionFormData.contractQuestions.find(
                existingQuestion =>
                    existingQuestion.planQuestion.id === question.id
            )
        }

        return generateQuestionFormData(question, existingQuestion)
    })

    // Add enrollment level questions
    if (
        enrollmentQuestions.length > 0 &&
        !questionData.value.terms.allowEmptyEnrollments
    ) {
        props.currentEnrollments.forEach(enrollment => {
            enrollment.enrollments.forEach((customer, enrollmentSlotIndex) => {
                const data = customer.data

                let questionEnrollment: { customerName: string } | undefined =
                    undefined
                if (data !== null) {
                    if (data.type === 'existing') {
                        questionEnrollment = {
                            customerName: data.commaName
                        }
                    } else {
                        if (data.lastName)
                            questionEnrollment = {
                                customerName:
                                    data.firstName + ' ' + data.lastName
                            }
                    }
                }

                // Find the existing enrollment slot
                const existingEnrollmentSlot =
                    props.questionFormData.enrollmentQuestions.find(
                        existingQuestions =>
                            existingQuestions.enrollmentSpecification
                                .enrollmentTypeId ===
                                enrollment.enrollmentType.id &&
                            existingQuestions.enrollmentSpecification
                                .sequence ===
                                enrollmentSlotIndex + 1
                    )

                let questions = []
                // Load existing data from contract if asked to
                if (loadContractData) {
                    questions = enrollmentQuestions.map(question => {
                        let existingValue: QuestionValue | undefined = undefined
                        if (data !== null && data.type === 'existing') {
                            const existingProperty =
                                contractData.value.properties.find(
                                    property =>
                                        property.planQuestion?.id ===
                                            question.id &&
                                        property.enrollment?.customer.id ===
                                            data.id
                                )
                            if (existingProperty !== undefined) {
                                existingValue = {
                                    propertyValue: existingProperty.value,
                                    planQuestionOptionIds:
                                        existingProperty.planQuestionOptions.map(
                                            option => option.id
                                        ),
                                    invalid: false
                                }
                            }
                        }

                        return generateQuestionFormData(question, existingValue)
                    })
                } else {
                    // Else load what is in questionsFormData
                    questions = enrollmentQuestions.map(question => {
                        let existingValue: QuestionValue | undefined = undefined
                        if (existingEnrollmentSlot !== undefined) {
                            // Find the existing question
                            const existingQuestion =
                                existingEnrollmentSlot.questions.find(
                                    existingQuestion =>
                                        existingQuestion.planQuestion.id ===
                                        question.id
                                )
                            if (existingQuestion !== undefined) {
                                existingValue = {
                                    propertyValue:
                                        existingQuestion.propertyValue,
                                    planQuestionOptionIds:
                                        existingQuestion.planQuestionOptionIds,
                                    invalid: existingQuestion.invalid
                                }
                            }
                        }

                        return generateQuestionFormData(question, existingValue)
                    })
                }

                planQuestionFormData.enrollmentQuestions.push({
                    enrollmentSpecification: {
                        enrollmentTypeName: enrollment.enrollmentType.singular,
                        enrollmentTypeId: enrollment.enrollmentType.id,
                        sequence: enrollmentSlotIndex + 1
                    },
                    enrollment: questionEnrollment,
                    questions: questions
                })
            })
        })
    }

    emit('update:questionFormData', planQuestionFormData)
}

function generateQuestionFormData(
    data: FragmentType<typeof AnswerPlanQuestionsQuestionFragment>,
    existingValue: QuestionValue | undefined
): ContractQuestionFormData {
    const question = getFragmentData(AnswerPlanQuestionsQuestionFragment, data)

    // If the question is required, we will set invalid to be true
    // Because, upon generation the question will be unanswered and invalid
    let initialInvalid = question.translatedConfig.required

    // If available set the default property value, and set invalid to false
    let initialPropertyValue = ''
    if (question.translatedConfig.__typename === 'PlanBooleanQuestion') {
        initialPropertyValue = '0'
    } else if (
        question.translatedConfig.__typename === 'PlanNumberQuestion' &&
        question.translatedConfig.defaultAnswerString !== null
    ) {
        initialPropertyValue = question.translatedConfig.defaultAnswerString
        initialInvalid = false
    } else if (
        question.translatedConfig.__typename === 'PlanTextQuestion' &&
        question.translatedConfig.defaultAnswerString !== null
    ) {
        initialPropertyValue = question.translatedConfig.defaultAnswerString
        initialInvalid = false
    } else if (
        question.translatedConfig.__typename === 'PlanParagraphQuestion' &&
        question.translatedConfig.defaultAnswerString !== null
    ) {
        initialPropertyValue = question.translatedConfig.defaultAnswerString
        initialInvalid = false
    }

    let initialOptionIds: string[] = []
    // If we have an existing value, use all the values from there
    if (existingValue !== undefined) {
        initialPropertyValue = existingValue.propertyValue
        initialOptionIds = existingValue.planQuestionOptionIds
        initialInvalid = existingValue.invalid
    }
    // Else set the default plan question option (and invalid to false) if available
    else if (
        question.translatedConfig.__typename === 'PlanSingleChoiceQuestion' &&
        question.translatedConfig.defaultPlanQuestionOptionId !== null
    ) {
        const defaultOptionId =
            question.translatedConfig.defaultPlanQuestionOptionId
        initialOptionIds[0] = defaultOptionId
        initialPropertyValue =
            question.planQuestionOptions.find(
                option => option.id === defaultOptionId
            )?.translatedLabel ?? ''
        initialInvalid = initialPropertyValue === ''
    } else if (
        question.translatedConfig.__typename === 'PlanMultipleChoiceQuestion' &&
        question.translatedConfig.defaultPlanQuestionOptionId !== null
    ) {
        const defaultOptionId =
            question.translatedConfig.defaultPlanQuestionOptionId
        initialOptionIds[0] = defaultOptionId
        initialPropertyValue =
            question.planQuestionOptions.find(
                option => option.id === defaultOptionId
            )?.translatedLabel ?? ''
        initialInvalid = initialPropertyValue === ''
    }

    let planQuestion = null
    if (question.translatedConfig.__typename === 'PlanNumberQuestion') {
        planQuestion = {
            id: question.id,
            questionType: question.translatedConfig.__typename,
            questionTitle: question.translatedConfig.title,
            questionBody: question.translatedConfig.body,
            numberConfig: {
                pricing:
                    question.translatedConfig.unitPricing === null
                        ? null
                        : {
                              feePricingTotal: new Decimal(
                                  question.translatedConfig.unitPricing.feePricingTotal
                              ),
                              duesPricingTotal: new Decimal(
                                  question.translatedConfig.unitPricing.duesPricingTotal
                              )
                          },
                min: question.translatedConfig.min,
                max: question.translatedConfig.max
            }
        }
    } else if (
        question.translatedConfig.__typename === 'PlanTextQuestion' ||
        question.translatedConfig.__typename === 'PlanParagraphQuestion'
    ) {
        // text and paragraph questions have the extra size field
        planQuestion = {
            id: question.id,
            questionType: question.translatedConfig.__typename,
            questionTitle: question.translatedConfig.title,
            questionBody: question.translatedConfig.body,
            size: question.translatedConfig.size
        }
    } else if (
        question.translatedConfig.__typename === 'PlanSingleChoiceQuestion'
    ) {
        // single choice questions have display mode and size fields
        planQuestion = {
            id: question.id,
            questionType: question.translatedConfig.__typename,
            questionTitle: question.translatedConfig.title,
            questionBody: question.translatedConfig.body,
            size: question.translatedConfig.size,
            questionInputType: question.translatedConfig.questionInputType
        }
    } else {
        planQuestion = {
            id: question.id,
            questionType: question.translatedConfig.__typename,
            questionTitle: question.translatedConfig.title,
            questionBody: question.translatedConfig.body
        }
    }

    return {
        planQuestion,
        planQuestionOptions: question.planQuestionOptions.map(option => {
            return {
                id: option.id,
                label: option.translatedLabel,
                pricing: {
                    feePricingTotal: new Decimal(
                        option.pricingConfig.feePricingTotal
                    ),
                    duesPricingTotal: new Decimal(
                        option.pricingConfig.duesPricingTotal
                    )
                }
            }
        }),
        propertyValue: initialPropertyValue,
        planQuestionOptionIds: initialOptionIds,
        required: question.translatedConfig.required,
        invalid: initialInvalid
    }
}

function updateContractValue(questionIndex: number, newValue: QuestionValue) {
    emit('update:questionFormData', {
        ...props.questionFormData,
        contractQuestions: [
            ...props.questionFormData.contractQuestions.slice(0, questionIndex),
            {
                ...props.questionFormData.contractQuestions[questionIndex],
                ...newValue
            },
            ...props.questionFormData.contractQuestions.slice(questionIndex + 1)
        ]
    })
}

function updateEnrollmentValue(
    enrollmentIndex: number,
    questionIndex: number,
    newValue: QuestionValue
) {
    const oldQuestions =
        props.questionFormData.enrollmentQuestions[enrollmentIndex].questions

    const newQuestions = [
        ...oldQuestions.slice(0, questionIndex),
        {
            ...oldQuestions[questionIndex],
            ...newValue
        },
        ...oldQuestions.slice(questionIndex + 1)
    ]

    emit('update:questionFormData', {
        ...props.questionFormData,
        enrollmentQuestions: [
            ...props.questionFormData.enrollmentQuestions.slice(
                0,
                enrollmentIndex
            ),
            {
                ...props.questionFormData.enrollmentQuestions[enrollmentIndex],
                questions: newQuestions
            },
            ...props.questionFormData.enrollmentQuestions.slice(
                enrollmentIndex + 1
            )
        ]
    })
}

const billingInterval = computed(() =>
    questionData.value.terms.billingType === 'RECURRING'
        ? questionData.value.terms.billingInterval
        : null
)

type EnrollmentLevelLoop = {
    enrollmentSpecification: {
        enrollmentTypeName: string
        enrollmentTypeId: string
        sequence: number
    }
    enrollment?: {
        customerName: string
    }
    questions: ContractQuestionFormData[]
    attachment: AttachmentRequirement | null
}

const enrollmentLevelLoops = computed<EnrollmentLevelLoop[]>(() => {
    return props.currentEnrollments.flatMap(enrollmentGroup => {
        return enrollmentGroup.enrollments.map((customer, enrollmentIndex) => {
            const enrollmentLevelQuestions =
                props.questionFormData.enrollmentQuestions.find(
                    entry =>
                        entry.enrollmentSpecification.enrollmentTypeId ===
                            enrollmentGroup.enrollmentType.id &&
                        entry.enrollmentSpecification.sequence ===
                            enrollmentIndex + 1
                )?.questions ?? []

            const data = customer.data

            let attachment: AttachmentRequirement | null = null
            let questionEnrollment: { customerName: string } | undefined =
                undefined
            if (data !== null) {
                let key
                if (data.type === 'existing') {
                    questionEnrollment = {
                        customerName: data.commaName
                    }

                    key = data.id
                } else {
                    if (data.lastName) {
                        questionEnrollment = {
                            customerName: data.firstName + ' ' + data.lastName
                        }
                    }

                    key = data.key
                }

                attachment =
                    props.enrollmentConstraints.enrollmentConstraintAttachmentLookup.value
                        .get(enrollmentGroup.enrollmentType.id)
                        ?.get(key) ?? null
            }

            const uploadingAttachment =
                props.enrollmentConstraints.enrollmentTypeAttachments.value.find(
                    attachment =>
                        attachment.enrollmentTypeId ===
                            enrollmentGroup.enrollmentType.id &&
                        attachment.enrollmentTypeSequence ===
                            enrollmentIndex + 1
                )
            if (uploadingAttachment !== undefined) {
                attachment = {
                    attachmentTypeId: uploadingAttachment.attachmentTypeId,
                    attachmentTypeName: uploadingAttachment.attachmentTypeName,
                    attachmentResult: {
                        title: uploadingAttachment.file.name,
                        valid: null, // Indicates pending upload
                        expiresAt: null // TODO: Calculate expiration for them here? Not shown in UI currently
                    }
                }
            }

            const value: EnrollmentLevelLoop = {
                enrollmentSpecification: {
                    enrollmentTypeName: enrollmentGroup.enrollmentType.singular,
                    enrollmentTypeId: enrollmentGroup.enrollmentType.id,
                    sequence: enrollmentIndex + 1
                },
                enrollment: questionEnrollment,
                questions: enrollmentLevelQuestions,
                attachment
            }

            return value
        })
    })
})

const showEnrollmentLevel = computed(() => {
    return enrollmentLevelLoops.value.some(
        enrollmentLevelLoop =>
            enrollmentLevelLoop.questions.length > 0 ||
            enrollmentLevelLoop.attachment !== null
    )
})

const showContractLevel = computed(() => {
    return (
        props.questionFormData.contractQuestions.length > 0 ||
        props.contractConstraints.contractConstraints.value.length > 0
    )
})
</script>
